import { useState } from 'react';
import { toast } from 'sonner';
import { Crosshair, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { measurePrecise, extRtk } from '../lib/precision';
import { uid } from '../lib/model';
import { cpTarget, cpEval, cpTol, cpLast, cpCm } from '../lib/controlPts';

const EMPTY = { name: '', fmt: 'utm', E: '', N: '', zone: '32', lat: '', lon: '', h: '' };

function AddForm({ t, onAdd }) {
  const [f, setF] = useState(EMPTY);
  const inp = (k, ph) => <input data-testid={`cp-${k}-input`} inputMode="decimal" placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />;
  const add = () => { const tg = cpTarget(f); if (!tg || !f.name.trim()) return toast.error(t.cpInvalid); onAdd({ id: uid('cp'), name: f.name.trim(), fmt: f.fmt, ...tg, meas: [] }); setF(EMPTY); };
  return (
    <div className="cp-form" data-testid="cp-form">
      <input data-testid="cp-name-input" placeholder={t.cpName} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <span className="chip-group">{['utm', 'll'].map((m) => <button key={m} type="button" className={`chip seg ${f.fmt === m ? 'on' : ''}`} data-testid={`cp-fmt-${m}`} onClick={() => setF({ ...f, fmt: m })}>{t['cpFmt_' + m]}</button>)}</span>
      {f.fmt === 'utm' ? <div className="cp-row">{inp('E', t.cpE)}{inp('N', t.cpN)}{inp('zone', t.cpZone)}</div> : <div className="cp-row">{inp('lat', t.cpLat)}{inp('lon', t.cpLon)}</div>}
      <div className="cp-row">{inp('h', t.cpH)}<button className="btn btn-primary btn-sm" data-testid="cp-add-btn" onClick={add}><Plus size={14} /> {t.cpAdd}</button></div>
    </div>
  );
}

// tolerance in cm: free typing, clamped when leaving the field
function TolInput({ label, id, v, onSet }) {
  const [txt, setTxt] = useState(null);
  const commit = () => { if (txt != null && +txt > 0) onSet(id, txt); setTxt(null); };
  return <label>{label}<input type="number" step="0.5" min="0.1" data-testid={`cp-tol-${id}-input`} value={txt ?? +(v * 100).toFixed(1)} onChange={(e) => setTxt(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && commit()} /></label>;
}

function Row({ t, cp, tol, onMeasure, onDel }) {
  const m = cpLast(cp), r = m && cpEval(cp, m.g, tol);
  return (
    <div className={`cp-item ${r ? (r.ok ? 'cp-ok' : 'cp-bad') : ''}`} data-testid={`cp-item-${cp.id}`}>
      <div className="cp-head"><b>{cp.name}</b><small>{cp.fmt === 'utm' ? `E ${cp.E.toFixed(3)} · N ${cp.N.toFixed(3)} (UTM ${cp.zone})` : `${cp.lat.toFixed(8)}, ${cp.lon.toFixed(8)}`}{cp.h != null ? ` · H ${cp.h.toFixed(3)} m` : ''}</small></div>
      {r && <p data-testid={`cp-result-${cp.id}`}><b>{r.ok ? t.cpPass : t.cpFail}</b> · {t.cpDH} {cpCm(r.dH)} (N {cpCm(r.dN)}, E {cpCm(r.dE)}) · {t.cpDV} {cpCm(r.dV)} · {new Date(m.ts).toLocaleString(t.locale)} · {(cp.meas || []).length}×{m.g.qs === 'exception' ? ` · ${t.qs_exception}` : ''}{m.g.sim ? ` · ${t.cpSimNo}` : ''}</p>}
      <div className="row">
        <button className="btn btn-green btn-sm" data-testid={`cp-measure-${cp.id}`} onClick={() => onMeasure(cp)}><Crosshair size={14} /> {t.cpMeasure}</button>
        <button className="icon-btn" data-testid={`cp-del-${cp.id}`} aria-label={t.cpDel} onClick={() => onDel(cp)}><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

// control point proof inside the GNSS dialog: known points re-measured, deviation stored for the report
export default function ControlPoints({ t, project, updateProject }) {
  const [open, setOpen] = useState(false);
  const list = project.controls || [], tol = cpTol(project);
  const save = (fn) => updateProject((p) => ({ ...p, controls: fn(p.controls || []) }));
  const setTol = (k, cm) => updateProject((p) => ({ ...p, cpTol: { ...cpTol(p), [k]: Math.max(0.001, (+cm || 0) / 100) } }));
  const onMeasure = async (cp) => {
    if (!extRtk()) return toast.error(t.cpNoRtk);
    const r = await measurePrecise();
    if (!r) return;
    const ev = cpEval(cp, r.gnss, tol);
    save((l) => l.map((x) => (x.id === cp.id ? { ...x, meas: [...(x.meas || []), { ts: Date.now(), g: r.gnss }] } : x)));
    (ev.ok ? toast.success : toast.error)(`${cp.name}: ${ev.ok ? t.cpPass : t.cpFail} · ${t.cpDH} ${cpCm(ev.dH)} · ${t.cpDV} ${cpCm(ev.dV)}`, { duration: 8000 });
  };
  return (
    <section className="cp-box" data-testid="cp-section">
      <button className="cp-toggle" data-testid="cp-toggle-btn" onClick={() => setOpen(!open)}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />} <b>{t.cpTitle}</b> <small>({list.length})</small></button>
      {open && <>
        <p className="gnss-hint">{t.cpHint}</p>
        <div className="cp-row cp-tol">
          <TolInput label={t.cpTolH} id="h" v={tol.h} onSet={setTol} />
          <TolInput label={t.cpTolV} id="v" v={tol.v} onSet={setTol} />
        </div>
        {list.map((cp) => <Row key={cp.id} t={t} cp={cp} tol={tol} onMeasure={onMeasure} onDel={(c) => window.confirm(t.cpDel + '?') && save((l) => l.filter((x) => x.id !== c.id))} />)}
        <AddForm t={t} onAdd={(cp) => save((l) => [...l, cp])} />
      </>}
    </section>
  );
}
